import {
    useAppInsightsContext,
    useTrackEvent,
} from '@microsoft/applicationinsights-react-js';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { Primitive } from 'type-fest';
import { z } from 'zod';

import {
    type Geolocation,
    LocationPicker,
    useGeolocation,
    useLocationService,
} from '@myphx-engineering/location-utils';
import {
    Anchor,
    Body1,
    Flex,
    Heading1,
    Stack,
    TextInput,
    useNavBar,
} from '@myphx-engineering/phx-design-system';
import {
    useForm,
    useHotkeys,
} from '@myphx-engineering/phx-design-system/hooks';

import { sendVerificationCodeApi } from '../../api/send-verification-code.api';
import { verifyAccountApi } from '../../api/verify-account.api';
import { PageLayout } from '../../components';
import {
    ErrorMap,
    type ErrorMessageTypes,
} from '../../components/alerts/errors/error-map';
import { SubmitButton } from '../../components/form-fields/SubmitButton';
import { telemetryInstance } from '../../instrumentation';
import type { ChallengeQuestionsFormModel } from '../../models/form.models';
import { routes } from '../../routes';
import type { VerifyAccountPageState } from '../verify-account/VerifyAccountPage';

const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

export type SignupChallengeQuestionsPageState = {
    phoneNumber: string;
    password: string;
    callBackUrl: string;
    userId?: string;
    email: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    partnerAccountConfiguration?: Record<string, Primitive>;
};

const SignupChallengeQuestions = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location?.state as SignupChallengeQuestionsPageState;
    const appInsights = useAppInsightsContext();
    const { setBackOverrideState } = useNavBar();

    const trackForSubmit = useTrackEvent(
        appInsights,
        'CHALLENGEQUESTIONS_CONTINUE_CLICK',
        {}
    );
    const trackForSkipSubmit = useTrackEvent(
        appInsights,
        'CHALLENGEQUESTIONS_SKIP_CLICK',
        {}
    );
    const trackForSignin = useTrackEvent(
        appInsights,
        'CHALLENGEQUESTIONS_SIGNIN_CLICK',
        {}
    );

    const form = useForm<ChallengeQuestionsFormModel>({
        schema: z.object({
            address: z.string().optional(),
            memberId: z.string({
                message: t('formErrors.formErrorMessage.invalidMemberId'),
            }),
        }),
    });

    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessageType, setErrorMessageType] = useState<
        ErrorMessageTypes | undefined
    >(undefined);

    const ErrorMessage = errorMessageType ? ErrorMap[errorMessageType] : null;

    const formAddress = form.values.address;
    const formMemberId = form.values.memberId;

    // Read parameters passed in redirect
    const userId = searchParams.get('user') ?? undefined;

    const onNavigateToSignIn = () => {
        trackForSignin({ message: 'Sign-in link is clicked' });
        navigate(
            { pathname: routes.signIn, search: window.location.search },
            { state: { backToSignUp: true } }
        );
    };

    const onNavigateBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    useEffect(() => {
        setBackOverrideState({
            overrideFn: onNavigateBack,
        });

        return () => setBackOverrideState({ overrideFn: null });
    }, [onNavigateBack, setBackOverrideState]);

    const skipFormSubmission = async () => {
        if (loading) {
            return;
        }

        setLoading(true);
        trackForSkipSubmit({ message: 'Challenge questions skipped' });

        const {
            firstName,
            lastName,
            dateOfBirth,
            phoneNumber,
            email,
            password,
            callBackUrl,
            userId,
            partnerAccountConfiguration,
        } = state;

        // Send OTP using old flow
        const sendVerificationCodeResponse =
            await sendVerificationCodeApi(phoneNumber);

        if (sendVerificationCodeResponse.success) {
            trackForSkipSubmit({ message: 'OTP sent successfully' });
            const verifyPageState: VerifyAccountPageState = {
                firstName,
                lastName,
                dateOfBirth,
                phoneNumber,
                email,
                password,
                callBackUrl,
                userId,
                isSignupFlow: true,
                partnerAccountConfiguration,
            };
            navigate(routes.verify, {
                state: verifyPageState,
            });
            return;
        }

        trackForSkipSubmit({ message: 'Failed to send OTP' });
        setLoading(false);
        setErrorMessageType(sendVerificationCodeResponse.error);
        telemetryInstance.logError({
            source: location.pathname,
            exception: new Error(
                sendVerificationCodeResponse.error ?? 'OTP request failed'
            ),
        });
    };

    const onFormSubmit = async (formData: ChallengeQuestionsFormModel) => {
        if (loading) {
            return;
        }

        setLoading(true);
        trackForSubmit({ message: 'Registration form is submitted' });

        const { address, memberId } = formData;
        const {
            firstName,
            lastName,
            dateOfBirth,
            phoneNumber,
            email,
            password,
            callBackUrl,
            userId,
            partnerAccountConfiguration,
        } = state;

        const {
            success: verifyAccountSuccess,
            userId: userIdFromVerifyResponse,
            error: verifyAccountError,
        } = await verifyAccountApi({
            firstName,
            lastName,
            email,
            dateOfBirth,
            phoneNumber,
            masterId: userId,
            memberId,
            address,
        });

        if (verifyAccountSuccess) {
            // Send OTP using old flow
            const sendVerificationCodeResponse =
                await sendVerificationCodeApi(phoneNumber);

            if (sendVerificationCodeResponse.success) {
                trackForSubmit({ message: 'OTP sent successfully' });
                const verifyPageState: VerifyAccountPageState = {
                    firstName,
                    lastName,
                    dateOfBirth,
                    phoneNumber,
                    email,
                    password,
                    callBackUrl,
                    userId: userId ?? userIdFromVerifyResponse,
                    isSignupFlow: true,
                    address: address ?? null,
                    partnerAccountConfiguration,
                };

                navigate(routes.verify, {
                    state: verifyPageState,
                });
                return;
            }

            trackForSubmit({ message: 'Failed to send OTP' });
            setLoading(false);
            setErrorMessageType(sendVerificationCodeResponse.error);
            telemetryInstance.logError({
                source: location.pathname,
                exception: new Error(
                    sendVerificationCodeResponse.error ?? 'OTP request failed'
                ),
            });

            return;
        }

        trackForSubmit({ message: 'Failed to verify account' });
        setLoading(false);
        setErrorMessageType(verifyAccountError);
        telemetryInstance.logError({
            source: location.pathname,
            exception: new Error(verifyAccountError),
        });

        if (verifyAccountError === '409_MultiplePatientsFoundForDemographic') {
            navigate(routes.signUpError);
        }
    };

    useHotkeys([['Enter', () => form.onSubmit(onFormSubmit)]]);

    const trackMemberId = useTrackEvent(
        appInsights,
        'SIGNUPCHALLENGEQUESTIONS_MEMBERID_CLICK',
        {}
    );
    const trackMailingAddress = useTrackEvent(
        appInsights,
        'SIGNUPCHALLENGEQUESTIONS_MAILINGADDRESS_CLICK',
        null
    );

    const { geolocation, setGeolocation } = useGeolocation();
    const locationService = useLocationService(accessToken, telemetryInstance);

    const handleSetGeolocation = useCallback(
        (location?: Geolocation) => {
            if (location?.address) {
                setGeolocation(location);
                form.setFieldValue('address', {
                    line1: location.address.line1,
                    line2: location.address.line2,
                    city: location.address.city,
                    state: location.address.state,
                    country: location.address.country,
                    postalCode: location.address.zip,
                });
            }
        },
        [setGeolocation, form]
    );

    const memberIdProps = form.getInputProps('memberId');
    memberIdProps.onFocus = () => {
        trackMemberId({ message: 'Member ID field is clicked' });
        form.getInputProps('memberId').onFocus();
    };

    return (
        <PageLayout
            loadingOverlay={loading}
            header={
                !loading
                    ? errorMessageType &&
                      ErrorMessage && (
                          <ErrorMessage onLinkClick={onNavigateToSignIn} />
                      )
                    : null
            }
            heading={
                <Stack gap="sm">
                    <Heading1>
                        {t('signUpChallengeQuestionsPage.planMember')}
                    </Heading1>
                    <Body1>
                        {t('signUpChallengeQuestionsPage.provideCredentials')}
                    </Body1>
                </Stack>
            }
            footer={
                <Stack gap="sm">
                    <SubmitButton
                        form="challenge-questions-form"
                        disabled={!formAddress && !formMemberId}
                    >
                        {t('common.continue')}
                    </SubmitButton>
                    <Flex justify="center" gap="xxxs">
                        <Body1>
                            {t('signUpChallengeQuestionsPage.notPlanMember')}
                        </Body1>
                        <Anchor
                            onClick={skipFormSubmission}
                            data-testid="skip-challenge-questions-link"
                        >
                            {t('signUpChallengeQuestionsPage.skip')}
                        </Anchor>
                    </Flex>
                </Stack>
            }
        >
            <form
                id="challenge-questions-form"
                onSubmit={form.onSubmit(onFormSubmit)}
            >
                <Stack gap="md" pt="md">
                    {userId ? null : (
                        <TextInput
                            key={form.key('memberId')}
                            name="memberId"
                            label={t('challengeQuestionsForm.memberId')}
                            placeholder={t(
                                'challengeQuestionsForm.enterMemberId'
                            )}
                            required
                            {...memberIdProps}
                        />
                    )}
                    <LocationPicker
                        geolocation={geolocation}
                        onFocus={() => trackMailingAddress(null)}
                        onLocationChange={handleSetGeolocation}
                        placeholder={t('challengeQuestionsForm.mailingAddress')}
                        locationService={locationService}
                    />
                </Stack>
            </form>
        </PageLayout>
    );
};

export default SignupChallengeQuestions;
